import { SkipNavContent } from "@reach/skip-nav";
import Button from "atoms/buttons/button";
import { LoaderStyles } from "atoms/constants/loader-styles";
import Loader from "atoms/loaders/loader";
import StickyNotification from "atoms/sticky-notification/sticky-notification";
import PublicationRecord from "models/view-models/publication-record";
import UnorderedList from "molecules/lists/unordered-list";
import MenuButton from "molecules/menu-button/menu-button";
import ChangeEditionModal from "organisms/modals/change-edition-modal/change-edition-modal";
import {
    BackButton,
    PublicationFavoriteButton,
    PublicationInfo,
} from "organisms/table-of-contents/table-of-content-meta-data";
import TableOfContents from "organisms/table-of-contents/table-of-contents";
import React from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { useErrorBoundary } from "utilities/hooks/use-error-boundary";
import { useLocalization } from "utilities/hooks/use-localization";
import useModalActions from "utilities/hooks/use-modal-actions";
import StringUtils from "utilities/string-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface PublicationPageProps {
    publication: PublicationRecord;
    publicationLoaded: boolean;
    pageErrors: string[];
    type?: "home" | "back";
    onBackClick?(): void;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASSNAME = "c-publication-page";
const DATA_TEST_ID_CHANGE_EDITION_BUTTON = "change-edition-button";
const DATA_TEST_ID_CHANGE_EDITION_BUTTON_MOBILE = `${DATA_TEST_ID_CHANGE_EDITION_BUTTON}-mobile`;
const DATA_TEST_ID_MOBILE_MENU_BUTTON = "mobile-menu-button";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const PublicationPage: React.FC<PublicationPageProps> = (
    props: PublicationPageProps
) => {
    const {
        publication,
        publicationLoaded,
        pageErrors,
        type = "home",
        onBackClick,
    } = props;

    // -----------------------------------------------------------------------------------------
    // #region Hooks
    // -----------------------------------------------------------------------------------------
    const { t } = useLocalization();

    // #endregion Hooks

    const {
        handleOpen: handleChangeEditionOpen,
        handleClose: handleChangeEditionClose,
        isOpen: showChangeEditionModal,
    } = useModalActions();

    useErrorBoundary(pageErrors);

    return (
        <div className={BASE_CLASSNAME}>
            <div className={`${BASE_CLASSNAME}__metadata`}>
                <div className={`${BASE_CLASSNAME}__metadata__top`}>
                    <BackButton type={type} onBackClick={onBackClick} />
                    <div className={`${BASE_CLASSNAME}__metadata__top__right`}>
                        <PublicationFavoriteButton publication={publication} />

                        <div
                            className={`${BASE_CLASSNAME}__metadata__top__right__more-menu`}>
                            <MenuButton
                                dataTestId={DATA_TEST_ID_MOBILE_MENU_BUTTON}>
                                <Button
                                    dataTestId={
                                        DATA_TEST_ID_CHANGE_EDITION_BUTTON_MOBILE
                                    }
                                    onClick={handleChangeEditionOpen}>
                                    {t("changeEdition")}
                                </Button>
                            </MenuButton>
                        </div>
                    </div>
                </div>
                {
                    // if
                    !publicationLoaded &&
                        CollectionUtils.isEmpty(pageErrors) && (
                            <Loader
                                accessibleText={t("loadingItem", {
                                    item: t("publication"),
                                })}
                                type={LoaderStyles.LinkGlyphGray}
                            />
                        )
                }
                {
                    // if
                    CollectionUtils.hasValues(pageErrors) && (
                        <UnorderedList
                            cssClassName={`${BASE_CLASSNAME}__errors`}
                            listItems={pageErrors}
                        />
                    )
                }
                {publicationLoaded && publication != null && (
                    <React.Fragment>
                        <PublicationInfo
                            coverImageFile={publication.coverImageFile}
                            code={publication.code ?? ""}
                            edition={publication.edition ?? ""}
                            title={publication.title ?? ""}
                            subTitle={publication.subTitle ?? ""}
                        />

                        <div className="c-publication-page__footer">
                            <Button
                                cssClassName="-link"
                                dataTestId={DATA_TEST_ID_CHANGE_EDITION_BUTTON}
                                onClick={handleChangeEditionOpen}>
                                {t("changeEdition")}
                            </Button>
                        </div>

                        <ChangeEditionModal
                            closeDialog={handleChangeEditionClose}
                            currentPublication={publication}
                            isVisible={showChangeEditionModal}
                        />
                    </React.Fragment>
                )}
            </div>
            <div className="c-publication-page__toc">
                <SkipNavContent>
                    <TableOfContents publication={publication} />
                </SkipNavContent>
            </div>
            {publicationLoaded &&
                StringUtils.isEmpty(publication?.publishedOn) && (
                    <StickyNotification>{t("unpublished")}</StickyNotification>
                )}
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default PublicationPage;

// #endregion Exports
